/* ENSSA Reporting — chrome + simple controls.
   Phase 4: thin wrappers around shadcn primitives. Public exports
   keep their legacy names + signatures so consumer pages don't change.
   Charts in Charts.tsx; tokens in tokens.ts. */

import React from "react";
import { Link } from "react-router-dom";
import { Info, Download, Printer } from "lucide-react";
import { TERMS, YEAR_LEVELS } from "@/data/tokens";
import { useTracker } from "@/hooks/useTracker";
import { riskLabel } from "@/lib/copy";
import type { CompletionStatus, RiskBand, TermId, YearLevel } from "@/types";

import { Card as UICard } from "@/components/ui/card";
import { Button as UIButton } from "@/components/ui/button";
import { UserMenu } from "@/components/branded/UserMenu";
import { RiskBadge as UIRiskBadge } from "@/components/ui/risk-badge";
import { StatusBadge as UIStatusBadge } from "@/components/ui/status-badge";
import {
  Select as UISelect,
  SelectContent as UISelectContent,
  SelectItem as UISelectItem,
  SelectTrigger as UISelectTrigger,
  SelectValue as UISelectValue,
} from "@/components/ui/select";
import {
  ToggleGroup as UIToggleGroup,
  ToggleGroupItem as UIToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  Tooltip as UITooltip,
  TooltipContent as UITooltipContent,
  TooltipProvider as UITooltipProvider,
  TooltipTrigger as UITooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Breadcrumb as UIBreadcrumb,
  BreadcrumbItem as UIBreadcrumbItem,
  BreadcrumbLink as UIBreadcrumbLink,
  BreadcrumbList as UIBreadcrumbList,
  BreadcrumbPage as UIBreadcrumbPage,
  BreadcrumbSeparator as UIBreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface CardProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

/* Card wrapper — delegates to shadcn Card; supports legacy `style` override. */
function Card({ children, style, className }: CardProps) {
  return <UICard style={style} className={className}>{children}</UICard>;
}

interface EyebrowProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

function Eyebrow({ children, style }: EyebrowProps) {
  /* fg-3 was bumped at the token level (colors_and_type.css) from ink-500
     (~4.9:1) to ink-600 (~7.6:1) so 11px uppercase eyebrow text passes
     Lighthouse APCA contrast across the whole app. */
  return <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-3)', ...style }}>{children}</div>;
}

interface YearChipProps {
  year: 'F' | 'Y1';
}

/* Year-level chip — small uppercase pill rendered next to the school name in
   the page eyebrow. Foundation uses --enssa-purple (#77468D) with white text
   → 6.4:1 (PASS AA). Year 1 uses color-mix(in oklab, --enssa-teal 80%, black)
   ≈ #007F66 with white text → ~5.0:1 (PASS AA). The 85% mix tested at
   ~4.47:1 — just under the 4.5:1 threshold — so we deepen to 80%. */
function YearChip({ year }: YearChipProps) {
  const isF = year === 'F';
  return (
    <span
      aria-label={`Year level: ${isF ? 'Foundation' : 'Year 1'}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--ink-0)',
        background: isF
          ? 'var(--enssa-purple)'
          : 'color-mix(in oklab, var(--enssa-teal) 80%, black)',
      }}
    >
      {isF ? 'Foundation' : 'Year 1'}
    </span>
  );
}

interface RiskBadgeProps {
  level: RiskBand;
  size?: 'sm' | 'md';
}

/* Branded RiskBadge thin-proxies to ui/risk-badge. The vocabulary-aware
   label (Acceptable / Low / Very low / Insufficient evidence in the
   default performance vocabulary) flows through via riskLabel() inside
   UIRiskBadge — including the per-student more_info case which here
   shows "Insufficient evidence", not the aggregate-context "Unknown".
   Aggregate / Sankey contexts that want "Unknown" should use
   domainRiskLabel(band) directly, not this branded badge. */
function RiskBadge({ level, size = 'md' }: RiskBadgeProps) {
  return <UIRiskBadge band={level} size={size} />;
}

interface StatusBadgeProps {
  status: CompletionStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  // Legacy StatusBadge: dot + label, no rounded pill bg.
  // We render the shadcn version but force the legacy "ghost" appearance via className overrides.
  return (
    <UIStatusBadge
      status={status}
      size="sm"
      className="bg-transparent !text-[var(--fg-2)] px-0 py-0 font-semibold border-0"
    />
  );
}

function MoreInfoFlag() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--fg-2)', background: 'var(--ink-100)', padding: '3px 10px', borderRadius: 999, border: '1px dashed var(--ink-300)' }}>
      <Info size={14} aria-hidden="true" /> {riskLabel('more_info')}
    </span>
  );
}

interface TermSelectorProps {
  value: TermId | string;
  onChange: (value: TermId) => void;
  /** Accessible name for the combobox (Lighthouse a11y + screen-reader gate).
      Defaults to "Screening term" but call sites can override (e.g. when the
      surrounding context already has a visible label). */
  ariaLabel?: string;
}

/* Term selector — shadcn Select, styled to match legacy native <select>. */
function TermSelector({ value, onChange, ariaLabel = 'Screening term' }: TermSelectorProps) {
  return (
    <UISelect value={value} onValueChange={(v) => onChange(v as TermId)}>
      <UISelectTrigger
        aria-label={ariaLabel}
        className="h-auto py-2 px-3.5 text-sm font-semibold bg-white text-[var(--fg-1)] border border-[var(--border-default)] rounded-md shadow-none"
      >
        <UISelectValue />
      </UISelectTrigger>
      <UISelectContent>
        {TERMS.map((t) => (
          <UISelectItem key={t.id} value={t.id}>{t.label}</UISelectItem>
        ))}
      </UISelectContent>
    </UISelect>
  );
}

interface TermToggleProps {
  value: TermId | string;
  onChange: (value: TermId) => void;
  ariaLabel?: string;
  /** Optional list of term ids to show when a dataset has only some screening windows. */
  availableTerms?: readonly (TermId | string)[];
  /** Optional uppercase eyebrow rendered to the left. Hidden by default. */
  label?: React.ReactNode;
}

/* Term toggle — inline pillbox alternative to TermSelector, matching the
   ViewSelector pattern in ClassPage. Same public API as TermSelector
   (value/onChange/ariaLabel) plus an optional uppercase eyebrow label. */
function TermToggle({ value, onChange, ariaLabel = 'Screening term', availableTerms, label = null }: TermToggleProps) {
  const visibleTerms = availableTerms?.length
    ? TERMS.filter((t) => availableTerms.includes(t.id))
    : TERMS;
  if (visibleTerms.length <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {label != null && (
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {label}
        </span>
      )}
      <UIToggleGroup
        type="single"
        size="sm"
        aria-label={ariaLabel}
        value={value}
        onValueChange={(v) => { if (v) onChange(v as TermId); }}
        style={{ background: '#fff', borderRadius: 7, padding: 2, border: '1px solid var(--border-default)', flexWrap: 'wrap' }}
      >
        {visibleTerms.map((t) => (
          <UIToggleGroupItem
            key={t.id}
            value={t.id}
            className={`view-toggle-item${value === t.id ? ' is-active' : ''}`}
            style={{
              background: value === t.id ? 'var(--enssa-aubergine)' : 'transparent',
              color: value === t.id ? '#fff' : 'var(--fg-1)',
              padding: '6px 12px',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              height: 'auto',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease, color 0.15s ease, transform 0.15s ease',
            }}
          >
            {t.label}
          </UIToggleGroupItem>
        ))}
      </UIToggleGroup>
    </div>
  );
}

interface YearLevelChipsProps {
  value: YearLevel;
  onChange: (value: YearLevel) => void;
  availableYears?: readonly YearLevel[] | undefined;
}

function YearLevelChips({ value, onChange, availableYears }: YearLevelChipsProps) {
  const all = YEAR_LEVELS.filter(y => !availableYears || availableYears.includes(y.id));
  if (all.length <= 1) return null;
  return (
    <UIToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => { if (v) onChange(v as YearLevel); }}
      className="bg-[var(--ink-50)] border border-[var(--border-subtle)] rounded-md p-0.5 gap-0 flex-shrink-0"
    >
      {all.map((o) => (
        <UIToggleGroupItem
          key={o.id}
          value={o.id}
          className={
            "h-auto px-3.5 py-1.5 text-[13px] font-semibold rounded-[6px] " +
            "data-[state=on]:bg-white data-[state=on]:text-[var(--fg-1)] " +
            "data-[state=on]:shadow-[var(--sh-1)] " +
            "data-[state=off]:bg-transparent data-[state=off]:text-[var(--fg-3)] " +
            "border-0 hover:bg-transparent"
          }
        >
          {o.label}
        </UIToggleGroupItem>
      ))}
    </UIToggleGroup>
  );
}

type BtnVariant = 'primary' | 'ghost' | 'neutral';

interface BtnProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: BtnVariant;
  children?: React.ReactNode;
}

/* <Btn> → shadcn <Button> with brand variants preserving forest-green palette. */
function Btn({ variant = 'primary', children, ...rest }: BtnProps) {
  const v: 'brand' | 'brand-outline' | 'brand-neutral' =
    variant === 'primary' ? 'brand' :
    variant === 'ghost' ? 'brand-outline' :
    'brand-neutral';
  return (
    <UIButton variant={v} size="legacy" {...rest}>
      {children}
    </UIButton>
  );
}

interface CSVExportButtonProps {
  scope?: string | undefined;
  onExport?: (() => void) | undefined;
}

function CSVExportButton({ scope, onExport }: CSVExportButtonProps) {
  const { pageType, track } = useTracker();

  return (
    <span data-action="export-csv">
      <Btn
        variant="neutral"
        onClick={() => {
          track("export_csv_clicked", { page_type: pageType ?? "unknown" });
          onExport?.();
        }}
      >
        <Download size={14} aria-hidden="true" /> Export CSV{scope ? ` · ${scope}` : ''}
      </Btn>
    </span>
  );
}

function PrintPDFButton() {
  const { routeContext, track } = useTracker();
  const onClick = () => {
    track("print_initiated", {
      student_id: routeContext?.params.studentId ?? null,
      class_id: routeContext?.params.classId ?? null,
    });

    const originalUrl = window.location.href;
    const printUrl = new URL(window.location.href);
    printUrl.searchParams.delete("compare");
    const changedForPopulationPrint = printUrl.href !== originalUrl;

    if (changedForPopulationPrint) {
      window.history.replaceState(window.history.state, "", printUrl);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }

    const restoreAfterPrint = () => {
      if (changedForPopulationPrint) {
        window.history.replaceState(window.history.state, "", originalUrl);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
      window.removeEventListener("afterprint", restoreAfterPrint);
    };
    window.addEventListener("afterprint", restoreAfterPrint);

    window.setTimeout(() => window.print(), changedForPopulationPrint ? 80 : 0);
  };
  return (
    <span
      data-action="print"
      title="Tip: in the print dialog, open More settings and uncheck 'Headers and footers' for a cleaner PDF."
    >
      <Btn variant="neutral" onClick={onClick}><Printer size={14} aria-hidden="true" /> Print / Save PDF</Btn>
    </span>
  );
}

export interface BreadcrumbItem {
  key: string;
  label: string;
  /** Phase 5: target path for declarative <Link> navigation. */
  href?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

/* Phase 5: breadcrumbs render <Link to={href}> for non-terminal items.
   Phase 7-followup: bumped link weight 600→700 and separator ink-300→400
   for legibility — feedback reported the previous render as faint. */
function Breadcrumbs({ items }: BreadcrumbsProps) {
  if (!items || items.length <= 1) return null;
  return (
    <UIBreadcrumb>
      <UIBreadcrumbList className="text-[13px] text-[var(--fg-2)]">
        {items.map((it, i) => {
          const last = i === items.length - 1;
          return (
            <React.Fragment key={it.key}>
              <UIBreadcrumbItem>
                {last || !it.href ? (
                  <UIBreadcrumbPage className="text-[var(--fg-1)] font-bold">{it.label}</UIBreadcrumbPage>
                ) : (
                  <UIBreadcrumbLink
                    asChild
                    className="!text-[var(--risk-on-track)] font-bold hover:!text-[var(--risk-on-track)] hover:underline"
                  >
                    <Link to={it.href} style={{ color: 'var(--risk-on-track)' }}>{it.label}</Link>
                  </UIBreadcrumbLink>
                )}
              </UIBreadcrumbItem>
              {!last && (
                <UIBreadcrumbSeparator className="text-[var(--ink-400)]">
                  <span aria-hidden="true">›</span>
                </UIBreadcrumbSeparator>
              )}
            </React.Fragment>
          );
        })}
      </UIBreadcrumbList>
    </UIBreadcrumb>
  );
}

interface WindowResources {
  __resources?: { solLogo?: string };
}

interface FooterStripProps {
  /* Role + onRoleChange are optional. When provided, the role switcher
     renders on the right of the footer; otherwise the footer shows just
     the logo (centred) — used by routes that shouldn't expose the role
     toggle (e.g. /print). */
  role?: RoleId | undefined;
  onRoleChange?: ((role: string) => void) | undefined;
}

function FooterStrip({ role, onRoleChange }: FooterStripProps = {}) {
  const solLogo = (typeof window !== 'undefined' && (window as Window & WindowResources).__resources?.solLogo) || '/assets/logos/sol-lockup-with-cis.png';
  const showSwitcher = !!(role && onRoleChange);
  const ROLES: ReadonlyArray<{ id: RoleId; label: string }> = [
    { id: 'teacher',   label: 'Teacher' },
    { id: 'school',    label: 'School' },
    { id: 'district',  label: 'District' },
    { id: 'programme', label: 'Dev' },
  ];
  return (
    <footer data-footer-strip style={{
      background: 'var(--enssa-aubergine)', color: '#fff',
      padding: '12px 0',
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        padding: '0 32px', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center',
        justifyContent: showSwitcher ? 'space-between' : 'center',
        gap: 16, flexWrap: 'wrap',
      }}>
        {/* Logo on the left (or centred when role switcher is hidden) */}
        <div style={{ background: '#fff', borderRadius: 6, padding: '6px 10px', height: 40, display: 'flex', alignItems: 'center' }}>
          <img src={solLogo} alt="Science of Learning · CIS" style={{ height: 26, width: 'auto', display: 'block' }} />
        </div>
        {/* Role switcher on the right (only when wired) — same primitive as the
            old header switcher (ToggleGroup type="single"), restyled for dark
            aubergine background. */}
        {showSwitcher && (
          <UIToggleGroup
            type="single"
            value={role}
            onValueChange={(v) => { if (v && onRoleChange) onRoleChange(v); }}
            aria-label="Viewing role"
            className="bg-white/10 border border-white/20 rounded-md p-0.5 h-auto gap-0 flex-shrink-0"
          >
            {ROLES.map((r) => (
              <UIToggleGroupItem
                key={r.id}
                value={r.id}
                aria-label={`View as ${r.label}`}
                className={
                  "h-auto px-3 py-[5px] text-xs font-bold rounded-[6px] " +
                  "data-[state=on]:bg-white data-[state=on]:text-[var(--enssa-aubergine)] " +
                  "data-[state=off]:bg-transparent data-[state=off]:text-white/70 " +
                  "border-0 hover:bg-white/5 hover:text-white"
                }
              >
                {r.label}
              </UIToggleGroupItem>
            ))}
          </UIToggleGroup>
        )}
      </div>
    </footer>
  );
}

type RoleId = 'teacher' | 'school' | 'district' | 'programme';

interface TopBarProps {
  visibleCrumbs?: BreadcrumbItem[];
}

/* TopBar — narrow utility strip rendered above PageHeader. Holds the
   breadcrumbs (left) and UserMenu (right). The explicit length>1 check
   here mirrors the early-return inside <Breadcrumbs />, but at this
   level it collapses the entire flex slot rather than just the inner
   element. Both checks coexist intentionally. */
function TopBar({ visibleCrumbs }: TopBarProps) {
  return (
    <div
      data-top-bar
      style={{
        background: 'var(--ink-50)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <div
        className="top-bar-row"
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '8px 32px',
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }} aria-label="ENSSA home">
              <img
                src="/assets/enssa-logo.png"
                alt="ENSSA — Early Number Sense Screener Australia"
                style={{ height: 28, width: 'auto', display: 'block' }}
              />
            </Link>
            {/* Permanent beta marker — decorative (aria-hidden); the
                accessible beta signal is the BetaNoticeBanner. */}
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'var(--beta-bg)',
                color: 'var(--beta-fg)',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                padding: '2px 6px',
                borderRadius: 999,
              }}
            >
              Beta
            </span>
          </div>
        </div>
        {visibleCrumbs && visibleCrumbs.length > 1 && (
          /* Direct row child so .top-bar-row can wrap it onto its own
             full-width line below 640px (shell.css) instead of squeezing
             a tall crumb column beside the logo. */
          <div className="top-bar-crumbs" style={{ minWidth: 0, flex: '1 1 auto' }}>
            <Breadcrumbs items={visibleCrumbs} />
          </div>
        )}
        <div style={{ flexShrink: 0, marginLeft: 'auto' }}>
          <UserMenu />
        </div>
      </div>
    </div>
  );
}

interface PageHeaderProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  controls?: React.ReactNode;
  stats?: React.ReactNode;
}

/* Role switcher moved to <FooterStrip /> and the user chip + breadcrumbs
   moved to <TopBar /> above. PageHeader is now pure page-identity
   (eyebrow + title + controls + stats). Top padding is 16px (was 8px
   while the user chip lived inside this header). */
function PageHeader({ eyebrow, title, controls, stats }: PageHeaderProps) {
  return (
    <header data-page-header style={{ padding: '16px 0 14px', borderBottom: '1px solid var(--border-subtle)', background: '#fff' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 32px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            <h1 style={{ fontSize: 28, fontWeight: 900, margin: '2px 0 0', letterSpacing: '-0.01em', textWrap: 'balance' }}>{title}</h1>
            {stats && (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>
                {stats}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>{controls}</div>
        </div>
      </div>
    </header>
  );
}

interface StatPillProps {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
}

function StatPill({ label, value, sub }: StatPillProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)', color: 'var(--fg-1)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

interface KPIProps {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  accent?: string;
}

function KPI({ label, value, sub, accent }: KPIProps) {
  return (
    <Card style={{ padding: 20 }}>
      <Eyebrow>{label}</Eyebrow>
      <div style={{ fontSize: 32, fontWeight: 900, margin: '6px 0 2px', letterSpacing: '-0.01em', color: accent || 'var(--fg-1)', fontFamily: 'var(--font-mono)' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>{sub}</div>}
    </Card>
  );
}

interface SectionProps {
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  hint?: React.ReactNode;
  controls?: React.ReactNode;
  filters?: React.ReactNode;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

function Section({ eyebrow, title, hint, controls, filters, children, style }: SectionProps) {
  return (
    <section style={{ marginBottom: 32, ...style }}>
      {filters && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          {filters}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 14, gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          {title && <h2 style={{ fontSize: 20, fontWeight: 700, margin: '4px 0 0', letterSpacing: '-0.005em' }}>{title}</h2>}
          {hint && <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--fg-2)', maxWidth: 720, lineHeight: 1.55 }}>{hint}</p>}
        </div>
        {controls && <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>{controls}</div>}
      </div>
      {children}
    </section>
  );
}

interface TitleWithInfoProps {
  title: React.ReactNode;
  tip: React.ReactNode;
}

function TitleWithInfo({ title, tip }: TitleWithInfoProps) {
  /* Glyph + tooltip styling matches the student-page distribution
     caption's (i) (see Charts.tsx PopulationPositionBar) so the same
     scale + cut-off explanation reads identically across class /
     school / district / student surfaces. */
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {title}
      <UITooltip>
        <UITooltipTrigger asChild>
          <button
            type="button"
            aria-label="More info"
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: 'transparent',
              color: 'var(--fg-3)',
              fontSize: 9,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
              flexShrink: 0,
              border: '1px solid var(--border-default)',
              padding: 0,
              fontFamily: 'inherit',
              lineHeight: 1,
              opacity: 0.7,
            }}
          >
            i
          </button>
        </UITooltipTrigger>
        <UITooltipContent
          side="top"
          sideOffset={8}
          className="text-xs leading-[1.55] py-2 px-3 normal-case tracking-normal"
        >
          {typeof tip === 'string' ? <div className="max-w-[320px]">{tip}</div> : tip}
        </UITooltipContent>
      </UITooltip>
    </span>
  );
}

interface ChartScaleTipProps {
  /** Primary explanation line. Wraps to keep tooltips readable. */
  scaleNote: string;
  /** Band cut-off explanation. */
  bandCutoffNote: string;
}

/* Two-line tooltip body shared by chart titles that pair the standard
   scale reference with the band cut-off explanation. Drops into
   `<TitleWithInfo tip={…} />` and matches the student-page chart's
   on-caption (i) tooltip. */
function ChartScaleTip({ scaleNote, bandCutoffNote }: ChartScaleTipProps) {
  return (
    <div className="max-w-[360px] whitespace-normal text-left leading-[1.55]">
      <div>{scaleNote}</div>
      <div className="mt-2 opacity-80">{bandCutoffNote}</div>
    </div>
  );
}

interface ScreeningDisclaimerProps {
  children?: React.ReactNode;
}

function ScreeningDisclaimer({ children }: ScreeningDisclaimerProps) {
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start',
      background: 'color-mix(in oklab, var(--enssa-mauve) 8%, white)',
      border: '1px solid color-mix(in oklab, var(--enssa-mauve) 25%, white)',
      borderRadius: 8, padding: '12px 16px',
      fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.55,
      maxWidth: 880,
    }}>
      <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--enssa-mauve)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800 }}>i</span>
      <div>
        {children || <>Screening results are a signal, not a diagnosis. Use them alongside classroom evidence and teacher judgement.</>}
      </div>
    </div>
  );
}

/* Re-export shadcn Tooltip primitives so consumers can build a11y-correct tips
   without ad-hoc `position: absolute` divs. */
export {
  UITooltipProvider as TooltipProvider,
  UITooltip as Tooltip,
  UITooltipTrigger as TooltipTrigger,
  UITooltipContent as TooltipContent,
};

export { Card, Eyebrow, YearChip, RiskBadge, StatusBadge, MoreInfoFlag, TermSelector, TermToggle, YearLevelChips, Btn, CSVExportButton, PrintPDFButton, Breadcrumbs, FooterStrip, TopBar, PageHeader, KPI, StatPill, Section, ScreeningDisclaimer, TitleWithInfo, ChartScaleTip };
